import { DragEventHandler, useCallback, useState } from 'react';
import { formatBytes } from '~/utils';

export const DEFAULT_MAX = 100 * 1024 * 1024; // 100 MB

interface UseDragAndDropFilesUploadProps {
  existingFiles?: { name: string }[];
  currentFiles?: File[];
  supportedFormats?: string[];
  maxSizeBytes?: number;
  onChange?: (files: File[]) => void;
  clearFilesAfterUpload?: boolean;
}

const useDragAndDropFilesUpload = ({
  existingFiles,
  currentFiles = [],
  supportedFormats,
  maxSizeBytes,
  onChange,
  clearFilesAfterUpload = false,
}: UseDragAndDropFilesUploadProps) => {
  const [files, setFiles] = useState<File[]>(currentFiles);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState<boolean>(false);

  const preventDragEvent = useCallback<DragEventHandler<HTMLDivElement>>((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleValidAppend = useCallback(
    (incoming: File[]) => {
      const next: File[] = [];
      const duplicateNames: string[] = [];
      let err: string | null = null;

      for (const f of incoming) {
        if (supportedFormats && !supportedFormats.some((ext) => f.name.endsWith(ext))) {
          err = `Only ${supportedFormats.join(', ')} are allowed.`;
          continue;
        }
        if (maxSizeBytes && f.size > maxSizeBytes) {
          err = `The file "${f.name}" exceeds the limit of ${formatBytes(maxSizeBytes)}.`;
          continue;
        }
        const duplicates = files.some(
          ({ name, size, lastModified }) => name === f.name && size === f.size && lastModified === f.lastModified,
        );
        const existingDuplicates = existingFiles?.some(({ name }) => name === f.name);

        if (!duplicates && !existingDuplicates) {
          next.push(f);
        } else {
          duplicateNames.push(f.name);
        }
      }

      if (duplicateNames.length) {
        err = `Duplicate files detected: ${duplicateNames.join(', ')}`;
      }

      if (next.length || err) {
        const merged = [...files, ...next];
        setFiles(merged);
        onChange?.(merged);
        if (clearFilesAfterUpload) {
          setFiles([]);
        }
        setError(err);
      }
    },
    [files, maxSizeBytes, onChange, supportedFormats, existingFiles],
  );

  const handleDragStart = useCallback<DragEventHandler<HTMLDivElement>>(
    (e) => {
      preventDragEvent(e);
      setDragActive(true);
    },
    [preventDragEvent],
  );

  const handleDragStop = useCallback<DragEventHandler<HTMLDivElement>>(
    (e) => {
      preventDragEvent(e);
      setDragActive(false);
    },
    [preventDragEvent],
  );

  const handleDrop = useCallback<DragEventHandler<HTMLDivElement>>(
    (e) => {
      handleDragStop(e);
      const dropped = Array.from(e.dataTransfer.files || []);
      if (dropped.length) handleValidAppend(dropped);
    },
    [handleDragStop, handleValidAppend],
  );

  return {
    listeners: {
      onDragEnter: handleDragStart,
      onDragOver: handleDragStart,
      onDragLeave: handleDragStop,
      onDrop: handleDrop,
    },
    handleValidAppend,
    files,
    setFiles,
    error,
    setError,
    dragActive,
  };
};

export default useDragAndDropFilesUpload;
